import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { 
  User, Mail, Phone,
  ChevronRight, ChevronLeft,
  DollarSign, CheckCircle2,
  ArrowRight
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import axios from "axios";
import { toast } from "sonner";

// Import extracted components and config
import { PAYOUT_CONFIG, formatCurrency, formatFullCurrency, calculateTotals } from "./calculator/config";
import SolutionCard from "./calculator/SolutionCard";
import ResultsDashboard from "./calculator/ResultsDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validate contact form
const validateContactInfo = (contactInfo) => {
  const errors = {};
  
  if (!contactInfo.fullName.trim()) {
    errors.fullName = 'Full name is required';
  }
  
  if (!contactInfo.email.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
    errors.email = 'Please enter a valid email';
  }
  
  if (!contactInfo.phone.trim()) {
    errors.phone = 'Phone number is required';
  }
  
  return errors;
};

// Progress Indicator Component
const ProgressIndicator = ({ step }) => (
  <div className="flex justify-center mb-10">
    <div className="flex items-center gap-4">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              step >= s ? 'bg-[#0099CC] text-white' : 'bg-slate-200 text-slate-500'
            }`}
          >
            {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
          </div>
          {s < 3 && (
            <div className={`w-16 h-1 mx-2 rounded ${step > s ? 'bg-[#0099CC]' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  </div>
);

// Step Labels Component
const StepLabels = ({ step }) => (
  <div className="flex justify-center mb-10">
    <div className="flex items-center gap-8 text-sm">
      <span className={step === 1 ? 'text-[#0099CC] font-semibold' : 'text-slate-400'}>
        Contact Info
      </span>
      <span className={step === 2 ? 'text-[#0099CC] font-semibold' : 'text-slate-400'}>
        Calculate Value
      </span>
      <span className={step === 3 ? 'text-[#0099CC] font-semibold' : 'text-slate-400'}>
        Your Results
      </span>
    </div>
  </div>
);

// Contact Step Component
const ContactStep = ({ contactInfo, setContactInfo, errors, onNext }) => (
  <motion.div
    key="step1"
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="max-w-xl mx-auto"
  >
    <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100">
      <h3 className="text-xl font-bold text-[#0F172A] mb-6 text-center">
        Let's start with your contact information
      </h3>
      
      <div className="space-y-5">
        <div>
          <Label htmlFor="fullName" className="text-slate-700 font-medium mb-2 block">
            Full Name *
          </Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              id="fullName"
              type="text"
              value={contactInfo.fullName}
              onChange={(e) => setContactInfo(prev => ({ ...prev, fullName: e.target.value }))}
              className={`pl-12 py-6 rounded-xl ${errors.fullName ? 'border-red-500' : ''}`}
              placeholder="Your full name"
              data-testid="calc-fullname"
            />
          </div>
          {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
        </div>
        
        <div>
          <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
            Email Address *
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              id="email"
              type="email"
              value={contactInfo.email}
              onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
              className={`pl-12 py-6 rounded-xl ${errors.email ? 'border-red-500' : ''}`}
              placeholder="your@email.com"
              data-testid="calc-email"
            />
          </div>
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
        
        <div>
          <Label htmlFor="phone" className="text-slate-700 font-medium mb-2 block">
            Phone Number *
          </Label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              id="phone"
              type="tel"
              value={contactInfo.phone}
              onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
              className={`pl-12 py-6 rounded-xl ${errors.phone ? 'border-red-500' : ''}`}
              placeholder="(555) 123-4567"
              data-testid="calc-phone"
            />
          </div>
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>
      </div>

      <Button
        onClick={onNext}
        className="w-full mt-8 bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-xl py-6 text-lg font-semibold"
        data-testid="calc-next-step1"
      >
        Calculate My Network Value
        <ChevronRight className="ml-2 w-5 h-5" />
      </Button>
    </div>
  </motion.div>
);

// Calculator Step Component
const CalculatorStep = ({ calculatorValues, totals, onValueChange, onBack }) => (
  <motion.div
    key="step2"
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
  >
    <div className="mb-8 text-center">
      <h3 className="text-xl font-bold text-[#0F172A] mb-2">
        How many businesses do you know that could benefit from these services?
      </h3>
      <p className="text-sm text-slate-500">
        Estimate conservatively. Even a few warm introductions could represent a major long-term income opportunity.
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-6 mb-8">
      {Object.keys(PAYOUT_CONFIG).map((key) => (
        <SolutionCard
          key={key}
          solution={PAYOUT_CONFIG[key]}
          solutionKey={key}
          values={calculatorValues[key]}
          onChange={onValueChange}
        />
      ))}
    </div>

    {totals.totalBusinesses > 0 && (
      <ResultsDashboard totals={totals} totalBusinesses={totals.totalBusinesses} />
    )}

    <div className="flex justify-between items-center mt-8">
      <Button
        variant="outline"
        onClick={onBack}
        className="rounded-xl py-6 px-8"
      >
        <ChevronLeft className="mr-2 w-5 h-5" />
        Back
      </Button>
      <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
        <Button
          disabled={totals.totalBusinesses === 0}
          className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-xl py-6 px-8 text-lg font-semibold disabled:opacity-50"
          data-testid="calc-next-step2"
        >
          Join Today
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </a>
    </div>
  </motion.div>
);

// Results Step Component
const ResultsStep = ({ calculatorValues, totals, isSubmitting, onSubmit, onBack }) => (
  <motion.div
    key="step3"
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="max-w-4xl mx-auto"
  >
    <ResultsDashboard totals={totals} totalBusinesses={totals.totalBusinesses} />

    <div className="mt-8 bg-white rounded-2xl p-6 border border-slate-200">
      <h4 className="font-bold text-[#0F172A] mb-4">Breakdown by Solution</h4>
      <div className="space-y-3">
        {Object.keys(PAYOUT_CONFIG).map((key) => {
          const config = PAYOUT_CONFIG[key];
          const values = calculatorValues[key];
          const solutionTotal = 
            (values.small || 0) * config.payouts.small.year1 +
            (values.medium || 0) * config.payouts.medium.year1 +
            (values.large || 0) * config.payouts.large.year1;
          
          const count = (values.small || 0) + (values.medium || 0) + (values.large || 0);
          
          if (count === 0) return null;
          
          const Icon = config.icon;
          return (
            <div key={key} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-[#0099CC]" />
                <span className="text-slate-700">{config.title}</span>
                <span className="text-xs text-slate-400">({count} business{count > 1 ? 'es' : ''})</span>
              </div>
              <span className="font-semibold text-[#0F172A]">{formatCurrency(solutionTotal)}/yr</span>
            </div>
          );
        })}
      </div>
    </div>

    <p className="text-center text-slate-500 text-sm mt-6">
      You make the introduction. Aspire Partners handles the presentation, closing, and fulfillment.
    </p>

    <div className="mt-8 space-y-4 text-center">
      <h4 className="text-2xl font-bold text-[#0F172A]">
        Join Today Risk FREE for 30 days
      </h4>
      <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
        <Button
          className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-xl py-6 px-10 text-lg font-semibold shadow-lg shadow-blue-500/30"
          data-testid="calc-submit"
        >
          Join Today
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </a>
      <p className="text-[#0099CC] font-semibold">
        100% Risk FREE · Try it FREE for 30 days · Then just $97/month
      </p>
    </div>

    <Button
      variant="ghost"
      onClick={onBack}
      className="w-full mt-4 text-slate-500"
    >
      <ChevronLeft className="mr-2 w-4 h-4" />
      Back to Calculator
    </Button>
  </motion.div>
);

// Success State Component
const SuccessState = ({ totals }) => (
  <motion.div
    key="success"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="max-w-xl mx-auto text-center"
  >
    <div className="bg-white rounded-3xl p-12 shadow-xl border border-slate-100">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-500" />
      </div>
      <h3 className="text-2xl font-bold text-[#0F172A] mb-4">
        Your Network Value Has Been Calculated!
      </h3>
      <p className="text-slate-600 mb-6">
        Based on your inputs, your network could generate up to{' '}
        <strong className="text-[#0099CC]">{formatFullCurrency(totals.year10)}</strong> over 10 years.
      </p>
      
      <div className="mt-8 pt-8 border-t border-slate-100">
        <h4 className="text-2xl font-bold text-[#0F172A] mb-2">
          Join Today Risk FREE for 30 days
        </h4>
        <p className="text-slate-500 text-sm mb-6">Start turning your network into recurring income</p>
        <a href="https://checkout.partnerwithaspirepartners.com/" target="_blank" rel="noopener noreferrer">
          <Button
            className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-xl py-6 px-10 text-lg font-semibold shadow-lg shadow-blue-500/30"
          >
            Join Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </a>
        <p className="mt-4 text-[#0099CC] font-semibold">
          100% Risk FREE · Try it FREE for 30 days · Then just $97/month
        </p>
      </div>
    </div>
  </motion.div>
);

// Main Calculator Component
const NetworkCalculator = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [contactInfo, setContactInfo] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  
  const [calculatorValues, setCalculatorValues] = useState({
    preventativeCare: { small: 0, medium: 0, large: 0 },
    technologySolutions: { small: 0, medium: 0, large: 0 },
    healthcareRCM: { small: 0, medium: 0, large: 0 },
    legalGuard: { small: 0, medium: 0, large: 0 }
  });

  const totals = calculateTotals(calculatorValues);

  const handleCalculatorChange = (solutionKey, size, value) => {
    setCalculatorValues(prev => ({
      ...prev,
      [solutionKey]: { ...prev[solutionKey], [size]: value }
    }));
  };

  const handleNext = () => {
    if (step === 1) {
      const validationErrors = validateContactInfo(contactInfo);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) return;
    }
    setStep(prev => Math.min(prev + 1, 3));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    const payload = {
      contact: contactInfo,
      calculator: calculatorValues,
      totals: {
        monthly: totals.monthly,
        year1: totals.year1,
        year5: totals.year5,
        year10: totals.year10,
        totalBusinesses: totals.totalBusinesses
      },
      source: 'network_calculator',
      utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || ''
    };

    try {
      await axios.post(`${API}/calculator-submissions`, payload);
      setIsSubmitted(true);
      toast.success("Your network value has been calculated! We'll be in touch soon.");
    } catch (error) {
      toast.error("There was an error submitting your information. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="calculator" className="py-16 md:py-24 bg-slate-50" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0099CC]/10 rounded-full mb-6">
            <DollarSign className="w-4 h-4 text-[#0099CC]" />
            <span className="text-sm font-medium text-[#0099CC]">Network Value Calculator</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">
            See How Much Your Network Is Worth
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            You already know business owners and decision-makers.
            <br />
            Now see what those introductions could be worth in recurring income.
          </p>
        </motion.div>

        <ProgressIndicator step={step} />
        <StepLabels step={step} />

        <AnimatePresence mode="wait">
          {step === 1 && (
            <ContactStep
              contactInfo={contactInfo}
              setContactInfo={setContactInfo}
              errors={errors}
              onNext={handleNext}
            />
          )}

          {step === 2 && (
            <CalculatorStep
              calculatorValues={calculatorValues}
              totals={totals}
              onValueChange={handleCalculatorChange}
              onBack={() => setStep(1)}
            />
          )}

          {step === 3 && !isSubmitted && (
            <ResultsStep
              calculatorValues={calculatorValues}
              totals={totals}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onBack={() => setStep(2)}
            />
          )}

          {isSubmitted && <SuccessState totals={totals} />}
        </AnimatePresence>

        {/* Disclaimer */}
        <p className="text-xs text-slate-400 text-center mt-12 max-w-4xl mx-auto leading-relaxed">
          Income potential with Aspire Partners is not guaranteed and will vary based on individual effort, experience, network, and market conditions. Any earnings examples or projections are for illustrative purposes only and should not be considered typical or promised results. Aspire Partners provides the platform and opportunity, but success depends on your ability to generate referrals and engage your network.
        </p>
      </div>
    </section>
  );
};

export default NetworkCalculator;
